import type { Tenant } from '../../../../configuration/types';
import { reportErrorToSentry } from '../../../../configuration/setup/sentry';
import type { DriverIdentificationFieldProps } from '../../../../types';
import { CountrySpecificDriverLicenseField } from './CountrySpecificDriverLicenseField';
import { EuTachographIdentificationField } from './EuTachographIdentificationField';
import { FormattedMessage } from 'react-intl';

export interface IdentificationFieldRowProps extends DriverIdentificationFieldProps {
    tenant: Tenant | null;
}

const DialogType = {
    COUNTRY_SPECIFIC: 0,
    EU_TACHOGRAPH: 1,
    UNKNOWN: 2,
} as const;

type DialogType = (typeof DialogType)[keyof typeof DialogType];

const mapTenantToDialogType = (tenant: Tenant | null): DialogType => {
    if (tenant?.startsWith('rio-eu.')) {
        return DialogType.EU_TACHOGRAPH;
    } else if (tenant?.startsWith('rio-brazil.')) {
        return DialogType.COUNTRY_SPECIFIC;
    } else {
        return DialogType.UNKNOWN;
    }
};

export const IDENTIFICATION_UNSUPPORTED_ERROR = 'intl-msg:error.fetch.message.F13';

export const isIdentificationSupportedTenant = (tenant: Tenant | null): boolean =>
    mapTenantToDialogType(tenant) !== DialogType.UNKNOWN;

export const IdentificationFieldRow = (props: IdentificationFieldRowProps) => {
    const dialogType = mapTenantToDialogType(props.tenant);

    if (dialogType === DialogType.EU_TACHOGRAPH) {
        return (
            <EuTachographIdentificationField
                className={props.className}
                parentDialogShown={props.parentDialogShown}
                existingDrivers={props.existingDrivers}
                onIdentificationChangeCallback={props.onIdentificationChangeCallback}
                currentDriver={props.currentDriver}
            />
        );
    } else if (dialogType === DialogType.COUNTRY_SPECIFIC) {
        return (
            <CountrySpecificDriverLicenseField
                className={props.className}
                parentDialogShown={props.parentDialogShown}
                existingDrivers={props.existingDrivers}
                onIdentificationChangeCallback={props.onIdentificationChangeCallback}
                currentDriver={props.currentDriver}
            />
        );
    } else {
        reportErrorToSentry(`Unknown tenant: ${props.tenant}`);
        return (
            <div className={'row'}>
                <div className={props.className || 'col-12 form-group'}>
                    <div className={'alert alert-warning'} role={'alert'}>
                        <FormattedMessage id={IDENTIFICATION_UNSUPPORTED_ERROR} />
                    </div>
                </div>
            </div>
        );
    }
};
